import { useState, useRef, useCallback, useMemo } from "react";
import { Download, FileText, ChevronLeft, Loader2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { BANK_PASSBOOK_BASE64 } from "./bankPassbook";
import { ContractForm, ContractFormToggle, DEFAULT_FORM } from "./ContractForm";

const LOGO_URL = "/assets/logo_full.png";
const STAMP_COMPANY = "/assets/stamp_company.png";
const STAMP_PERSONAL = "/assets/stamp_personal.png";

const BASE_FEE = 78000;
const GROWTH_FEE = 56000;
const LIGHT_FEE = 38000;
const GROWTH_ORIGINAL_FEE = 66000;
const ORIGINAL_FEE = 108000;
const ACTIVATION_FEE = 50000;
const fmt = (n) => `NT$ ${n.toLocaleString()}`;

// ---------- shared styles ----------
const S = {
  secTitle: { fontSize: "13px", fontWeight: "700", color: "#0A2540", marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px" },
  dot: (bg) => ({ width: "16px", height: "16px", background: bg, borderRadius: "4px", flexShrink: 0 }),
  table: { width: "100%", borderCollapse: "collapse", fontSize: "12px" },
  label: { padding: "8px 12px", border: "1px solid #e2e8f0", background: "#f8f9fa", fontWeight: "600", color: "#0A2540", width: "140px" },
  cell: { padding: "8px 12px", border: "1px solid #e2e8f0", color: "#0A2540" },
  head: (bg = "#0A2540") => ({ padding: "8px 12px", border: "1px solid #e2e8f0", background: bg, color: "white", fontWeight: "700", fontSize: "12px" }),
  body: { fontSize: "11px", color: "#475569", lineHeight: "1.8" },
  termsBody: { fontSize: "13px", color: "#475569", lineHeight: "1.75" },
  clause: { marginBottom: "8px" },
  clauseHi: { marginBottom: "10px", background: "#FFF3E6", borderLeft: "3px solid #FF6B00", padding: "8px 12px", borderRadius: "4px", color: "#0A2540", fontWeight: "600" },
  clauseMed: { marginBottom: "10px", background: "#FFFBEE", borderLeft: "3px solid #F5C24A", padding: "8px 12px", borderRadius: "4px", color: "#334155", fontWeight: "500" },
};

// Blank fill-in line or the value
const B = ({ v, w = 140 }) =>
  v && v.toString().trim() ? (
    <span style={{ fontWeight: "500", color: "#0A2540" }}>{v}</span>
  ) : (
    <span style={{ display: "inline-block", minWidth: `${w}px`, borderBottom: "1px solid #94a3b8" }}>&nbsp;</span>
  );

// 1/2/3 年圈選
const YearCircles = ({ selected }) => (
  <span>
    {[1, 2, 3].map((y) => (
      <span key={y} style={{ marginRight: "16px", fontWeight: y === selected ? "800" : "400", color: y === selected ? "#FF6B00" : "#475569" }}>
        {y === selected ? "●" : "○"} {y} 年
      </span>
    ))}
    <span style={{ fontSize: "10px", color: "#94a3b8" }}>（請圈選）</span>
  </span>
);

// 12/24/36 個月圈選（對應 1/2/3 年，用於三方案比較表）
const MonthCircles = ({ months, selectedIdx, active }) => (
  <span style={{ display: "inline-flex", flexWrap: "wrap", gap: "6px", justifyContent: "center" }}>
    {months.map((m, i) => (
      <span key={m} style={{
        fontWeight: active && i === selectedIdx ? "800" : "400",
        color: active && i === selectedIdx ? "#FF6B00" : "#475569",
      }}>
        {active && i === selectedIdx ? "●" : "○"} {m} 個月
      </span>
    ))}
  </span>
);

const SERVICES = [
  ["專業客服團隊", "千萬營收培訓客服輪班接單，服務時段依方案等級（詳見下方「方案服務等級」）"],
  ["客服聚焦小組", "每月召開一次聚焦會議，持續優化接單流程與服務品質"],
  ["KOL 導流", "網紅開箱導流（保障隻數依方案等級，詳見「方案服務等級」）"],
  ["素材拍攝與投廣", "專業素材拍攝 + Meta/Google 廣告精準投放"],
  ["Google 評論經營", "目前客戶平均評分 4.9 以上，圖文並茂，大幅提升信賴度與吸引度"],
  ["客單價住房率提升方案包裝設計", "提升平日住房率與整體營收，客單價平均提升 10–25%（已有多個成功案例）"],
  ["PMS 系統 + 專人收款整理", "協助訂金與收款整理；簽約優先使用信用卡金流自動對帳系統"],
  ["會員 CRM 管理", "所有 IG／FB／LINE@ 業主全程可監督，由乙方執行管理與經營；後續優先升級 AI CRM"],
  ["社群小編經營", "專屬小編每日發文、限動、私訊回覆 — IG／FB／LINE@ 一手包辦"],
  ["Facebook 粉絲專頁建置與經營", "帳號建置、內容企劃、精準廣告投放"],
  ["Instagram 官方帳號建置與引流", "帳號經營、網紅合作導流（500+ KOL 資源）"],
  ["LINE@ 官方帳號建置", "選單設計、自動回覆、每日成交回報機器人"],
  ["整體行銷漏斗規劃", "Google SEO 優化、Meta 廣告投放與優化"],
  ["AI 智能接單系統（Beta 開發中）", "上線後簽約客戶優先使用，實現 24/7 全天候服務"],
  ["人客多 PMS 管理系統", "包棟專屬訂房、營收報表、住房率分析、定價策略優化"],
  ["OTA 平台管理", "Booking.com、Agoda、Trip.com 等平台上架與優化"],
  ["LINE 每日回報機器人", "每日即時掌握營運狀況"],
];

const SERVICE_HL = ["社群小編經營", "客單價住房率提升方案包裝設計"];

const OWNER_DUTIES = ["餐食準備", "約定之一泊三食、一泊二食之餐食安排", "相關人力配置", "餐食體驗設計與執行", "床單床具清洗", "備品準備與補充", "管家人事費用", "現場物業水電維護", "上述所有相關成本"];

// 各方案服務等級（旗艦 plan1 / 成長 plan5 / 輕量 plan4）
const PLAN_SERVICE = {
  plan1: [
    ["專業客服時段", "客服接單延長至 23:00（週一～五 10:00–23:00；週六、日 10:00–20:00），接住夜間 21–23 新客高峰"],
    ["廣告投放", "廣告投手＋廣告素材製作，廣告費由乙方全額負擔"],
    ["社群經營", "社群小編經營 IG／FB／LINE@"],
    ["OTA 代操", "OTA 上架管理與代操"],
    ["保障影片", "保障影片 60 隻（價值約 NT$60 萬）；依實際情況調整，避免影響營收，不一定拍滿"],
    ["KOL 導流", "KOL 55 位以上開箱導流"],
    ["定價策略", "客戶經理・多棟庫存統一調度與定價優化（跨棟導流），隨季節與熱度動態調整房價"],
    ["優先排程", "KOL 與拍攝檔期優先排程"],
    ["Google 評價優化", "Google 評價優化（團隊用）"],
    ["進單分析", "每月提供進單客戶分析報告＋競品房價監測"],
    ["方案包裝", "客單價提升方案包裝設計（客單價平均提升 10–25%）"],
  ],
  plan5: [
    ["專業客服時段", "客服接單延長至 23:00（週一～五 10:00–23:00；週六、日 10:00–20:00），接住夜間 21–23 新客高峰"],
    ["廣告投放", "廣告投手＋廣告素材製作，廣告費由乙方全額負擔"],
    ["社群經營", "社群小編經營 IG／FB／LINE@"],
    ["OTA 代操", "OTA 上架管理與代操"],
    ["保障影片", "保障影片 48 隻（價值約 NT$48 萬）；依實際情況調整，避免影響營收，不一定拍滿"],
    ["KOL 導流", "KOL 40 位以上開箱導流"],
    ["定價策略", "客戶經理協助庫存與定價優化，隨季節與熱度動態調整房價"],
    ["Google 評價優化", "Google 評價優化（個人用）"],
    ["進單分析", "每月提供進單客戶分析報告"],
    ["方案包裝", "客單價提升方案包裝設計（客單價平均提升 10–25%）"],
  ],
  plan4: [
    ["專業客服時段", "客服接單 10:00–21:00（週一～五）；週六、日 10:00–20:00"],
    ["廣告投放", "廣告投手＋廣告素材製作，廣告費由乙方全額負擔"],
    ["社群經營", "社群小編經營 IG／FB／LINE@"],
    ["OTA 代操", "OTA 上架管理與代操"],
    ["保障影片", "保障影片 20 隻（價值約 NT$20 萬）；依實際情況調整，避免影響營收，不一定拍滿"],
    ["KOL 導流", "KOL 15 位以上開箱導流"],
    ["定價策略", "客戶經理協助優化庫存與定價"],
    ["Google 評價優化", "Google 評價優化（個人用）"],
    ["方案包裝", "客單價提升方案包裝設計（客單價平均提升 10–25%）"],
  ],
};

const ContractPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const contractRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const planParam = searchParams.get("plan");
  const [selectedPlan, setSelectedPlan] = useState(
    ["plan1", "plan2", "plan3", "plan4", "plan5"].includes(planParam) ? planParam : "plan1"
  );
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);

  const unitsParam = parseInt(searchParams.get("units"), 10);
  const [units, setUnits] = useState(Number.isFinite(unitsParam) && unitsParam >= 1 && unitsParam <= 20 ? unitsParam : 1);

  const setField = useCallback((key, value) => setForm((f) => ({ ...f, [key]: value })), []);

  const today = useMemo(() => new Date(), []);
  const signDateObj = form.signDate ? new Date(form.signDate + "T00:00:00") : null;
  const dateStr = signDateObj
    ? `${signDateObj.getFullYear()} 年 ${signDateObj.getMonth() + 1} 月 ${signDateObj.getDate()} 日`
    : `${today.getFullYear()} 年 ${today.getMonth() + 1} 月 ${today.getDate()} 日`;

  const extraUnits = Math.max(0, units - 1);
  const monthlyFee = BASE_FEE + extraUnits * 5000;
  const years = form.contractYears || 1;
  const guaranteed = parseInt(form.guaranteedRevenue, 10);
  const hasGuaranteed = Number.isFinite(guaranteed) && guaranteed > 0;
  const exG = hasGuaranteed ? guaranteed : 150000;
  const exTotal = exG + 700000;
  const minRev = parseInt(form.minRevenue, 10);
  const hasMinRev = Number.isFinite(minRev) && minRev > 0;

  // 階梯式月費（月付方案專用；預設關閉）
  const revTierThreshold = parseInt(form.revTierThreshold, 10);
  const revTierBelowFee = parseInt(form.revTierBelowFee, 10);
  const revTierBelowCommission = parseFloat(form.revTierBelowCommission);
  const isFeeCommissionPlan = ["plan1", "plan5", "plan4"].includes(selectedPlan);
  const showRevTier =
    isFeeCommissionPlan &&
    !!form.revTierEnabled &&
    Number.isFinite(revTierThreshold) && revTierThreshold > 0 &&
    Number.isFinite(revTierBelowFee) && revTierBelowFee >= 0 &&
    Number.isFinite(revTierBelowCommission) && revTierBelowCommission >= 0;

  const planNames = { plan1: "旗艦方案", plan5: "人客多成長方案", plan4: "輕量方案", plan2: "保障分潤方案", plan3: "回租方案" };
  const planName = planNames[selectedPlan];

  const isLight = selectedPlan === "plan4";
  const isGrowth = selectedPlan === "plan5";
  const isFlagship = selectedPlan === "plan1";
  const isFeeCommission = isFlagship || isGrowth || isLight; // 有月費＋抽成之方案
  const planBaseFee = isLight ? LIGHT_FEE : isGrowth ? GROWTH_FEE : BASE_FEE;
  const planMonthlyFee = planBaseFee + extraUnits * 5000;
  const commissionRate = isLight ? 25 : 20;
  const penaltyBase = planBaseFee;

  const penalty = (y) => penaltyBase * y + ACTIVATION_FEE;

  const csHours = (isFlagship || isGrowth) ? "10:00–23:00" : "10:00–21:00";
  const companyDuties = [
    "合約內容約定之行銷相關所有成本",
    "廣告費用",
    "網紅合作費用",
    `客服接單服務（每日 ${csHours}，週六、日 10:00–20:00）`,
    "社群媒體經營費用",
    "客單價住房率提升方案包裝設計（提高客單價）",
    "其他行銷相關支出",
  ];

  const handleDownloadPDF = useCallback(async () => {
    if (!contractRef.current || isGenerating) return;
    setIsGenerating(true);
    try {
      const element = contractRef.current;

      // Wait for all fonts (Noto Sans TC) to fully load before measuring — otherwise
      // measurement happens with fallback fonts, then html2canvas renders with the
      // real fonts, which shifts text wrapping and mis-aligns page-break spacers.
      if (document.fonts && document.fonts.ready) {
        try { await document.fonts.ready; } catch { /* noop */ }
      }

      const images = element.querySelectorAll("img");
      await Promise.all(
        Array.from(images).map((img) => {
          if (img.complete && img.naturalWidth > 0) return Promise.resolve();
          return new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
            const src = img.src;
            img.src = "";
            img.src = src;
          });
        })
      );
      // Two rAFs → guarantees a fresh layout after fonts + images settle
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      // Smart page-break: push blocks that straddle an A4 boundary to the next page.
      // Iterate in DOM order; getBoundingClientRect() is called fresh so each iteration
      // reflects cumulative spacer offsets from previous iterations.
      const PAGE_HEIGHT_PX = Math.floor(794 * (297 / 210));
      const sections = element.querySelectorAll("[data-pdf-section], [data-pdf-block]");
      const spacers = [];
      const containerTop = element.getBoundingClientRect().top;
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const sectionTop = rect.top - element.getBoundingClientRect().top;
        const sectionBottom = sectionTop + rect.height;
        const pageStart = Math.floor(sectionTop / PAGE_HEIGHT_PX);
        const pageEnd = Math.floor((sectionBottom - 1) / PAGE_HEIGHT_PX);
        if (pageEnd > pageStart && rect.height < PAGE_HEIGHT_PX * 0.9) {
          const nextPageTop = (pageStart + 1) * PAGE_HEIGHT_PX;
          const spacerHeight = nextPageTop - sectionTop + 24;
          const spacer = document.createElement("div");
          spacer.style.height = `${spacerHeight}px`;
          spacer.style.width = "100%";
          spacer.setAttribute("data-pdf-spacer", "true");
          section.parentNode.insertBefore(spacer, section);
          // Force synchronous layout so the following iterations see the shift
          void section.getBoundingClientRect();
          spacers.push(spacer);
        }
      });
      void containerTop; // reserved for potential future use
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      const canvas = await html2canvas(element, {
        scale: 2,
        allowTaint: true,
        useCORS: false,
        logging: false,
        backgroundColor: "#ffffff",
        windowWidth: 794,
        imageTimeout: 15000,
        onclone: (clonedDoc) => {
          // Ensure fonts are also considered ready in the clone
          if (clonedDoc.fonts && clonedDoc.fonts.ready) {
            return clonedDoc.fonts.ready;
          }
        },
      });
      spacers.forEach((s) => s.remove());

      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
      while (heightLeft > 0) {
        position = position - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }
      const d = signDateObj || today;
      const dateTag = `${d.getFullYear()}${(d.getMonth() + 1).toString().padStart(2, "0")}${d.getDate().toString().padStart(2, "0")}`;
      const propTag = form.propertyName.trim() ? `${form.propertyName.trim()}_` : "";
      pdf.save(`Lakeze_${propTag}合約報價單_${planName}_${dateTag}.pdf`);
    } catch {
      alert("PDF 產生失敗，請重試");
    } finally {
      setIsGenerating(false);
    }
  }, [isGenerating, planName, today, form.signDate, form.propertyName]);

  const specialLines = form.specialTerms.split("\n").map((l) => l.trim()).filter(Boolean);

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Top bar */}
      <div className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <button
            onClick={() => navigate("/")}
            data-testid="contract-back-btn"
            className="flex items-center gap-1 text-[#0A2540] hover:text-[#FF6B00] transition-colors font-medium text-sm shrink-0"
          >
            <ChevronLeft className="w-4 h-4" />
            回首頁
          </button>

          <div className="flex items-center gap-2">
            <ContractFormToggle open={showForm} onToggle={() => setShowForm(!showForm)} />
            <select
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value)}
              data-testid="contract-plan-selector"
              className="text-sm border border-slate-300 rounded-lg px-2 sm:px-3 py-2 bg-white text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/30"
            >
              <option value="plan1">旗艦方案（高階）</option>
              <option value="plan5">人客多成長方案（中階）</option>
              <option value="plan4">輕量方案（入門）</option>
              <option value="plan2">保障分潤方案</option>
              <option value="plan3">回租方案</option>
            </select>
            <button
              onClick={handleDownloadPDF}
              disabled={isGenerating}
              data-testid="contract-download-btn"
              className="flex items-center gap-2 bg-[#FF6B00] text-white hover:bg-[#E65A00] disabled:opacity-60 transition-all px-3 sm:px-5 py-2 rounded-lg font-bold text-sm"
            >
              {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              <span className="hidden sm:inline">下載 PDF</span>
              <span className="sm:hidden">PDF</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto py-8 px-4">
        {showForm && (
          <ContractForm form={form} setField={setField} selectedPlan={selectedPlan} units={units} setUnits={setUnits} />
        )}

        <div className="md:hidden text-center text-xs text-slate-400 mb-3">
          可左右滑動預覽合約，下載 PDF 為完整 A4 版面
        </div>
        <div className="overflow-x-auto">
          <div
            ref={contractRef}
            className="bg-white shadow-xl mx-auto"
            style={{ width: "794px", padding: "60px 60px 40px", fontFamily: "'Noto Sans TC', sans-serif" }}
          >
            {/* ===== Header ===== */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "32px", borderBottom: "3px solid #FF6B00", paddingBottom: "20px" }}>
              <div>
                <img src={LOGO_URL} alt="Lakeze" style={{ height: "44px", marginBottom: "8px" }} />
                <div style={{ fontSize: "11px", color: "#475569", lineHeight: "1.6" }}>
                  人客多股份有限公司<br />
                  統一編號：00224568
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "24px", fontWeight: "800", color: "#0A2540", letterSpacing: "2px" }}>合作方案報價單</div>
                <div style={{ fontSize: "11px", color: "#475569", marginTop: "4px" }}>QUOTATION / CONTRACT</div>
                <div style={{ fontSize: "12px", color: "#0A2540", marginTop: "8px", fontWeight: "600" }}>日期：{dateStr}</div>
              </div>
            </div>

            {/* ===== 合約雙方 ===== */}
            <div data-pdf-section="parties" style={{ marginBottom: "28px" }}>
              <div style={S.secTitle}>
                <FileText style={{ width: "16px", height: "16px", color: "#FF6B00" }} />
                合約雙方
              </div>
              <table style={S.table}>
                <tbody>
                  <tr><td colSpan="4" style={S.head("#0A2540")}>甲方資料</td></tr>
                  <tr>
                    <td style={S.label}>負責人名稱</td>
                    <td style={S.cell}><B v={form.ownerName} /></td>
                    <td style={{ ...S.label }}>身分證字號 / 統一編號</td>
                    <td style={S.cell}><B v={form.ownerId} /></td>
                  </tr>
                  <tr>
                    <td style={S.label}>公司</td>
                    <td style={S.cell}><B v={form.company} /></td>
                    <td style={S.label}>民宿執照登記</td>
                    <td style={S.cell}>{form.license.trim() ? <B v={form.license} /> : <span style={{ fontSize: "10px", color: "#94a3b8" }}>名稱與編號（若無則免填）</span>}</td>
                  </tr>
                  <tr>
                    <td style={S.label}>聯絡電話</td>
                    <td style={S.cell}><B v={form.phone} /></td>
                    <td style={S.label}>電子郵件</td>
                    <td style={S.cell}><B v={form.email} /></td>
                  </tr>
                  <tr>
                    <td style={S.label}>戶籍地址</td>
                    <td colSpan="3" style={S.cell}><B v={form.address} w={300} /></td>
                  </tr>
                  <tr>
                    <td style={S.label}>物件名稱</td>
                    <td colSpan="3" style={S.cell}><B v={form.propertyName} w={300} /></td>
                  </tr>
                  <tr>
                    <td style={S.label}>物件地址</td>
                    <td colSpan="3" style={S.cell}><B v={form.propertyAddress} w={300} /></td>
                  </tr>
                  <tr><td colSpan="4" style={S.head("#FF6B00")}>乙方資料</td></tr>
                  <tr>
                    <td style={S.label}>乙方</td>
                    <td style={{ ...S.cell, fontWeight: "500" }}>人客多股份有限公司</td>
                    <td style={S.label}>統一編號</td>
                    <td style={S.cell}>00224568</td>
                  </tr>
                  <tr>
                    <td style={S.label}>簽約顧問</td>
                    <td style={S.cell}><B v={form.consultant} /></td>
                    <td style={S.label}>電子郵件</td>
                    <td style={{ ...S.cell, fontSize: "11px" }}><B v={form.consultantEmail} w={180} /></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ===== 合作方案 ===== */}
            <div data-pdf-section="plan" style={{ marginBottom: "28px" }}>
              <div style={S.secTitle}>
                <div style={S.dot("#FF6B00")}></div>
                合作方案：{planName}
              </div>
              <table style={S.table}>
                <tbody>
                  <tr>
                    <td style={{ ...S.label, background: "#0A2540", color: "white" }}>方案名稱</td>
                    <td style={{ ...S.cell, fontWeight: "700", color: "#FF6B00", fontSize: "14px" }}>{planName}</td>
                  </tr>
                  {selectedPlan === "plan1" && (
                    <>
                      <tr>
                        <td style={S.label}>啟動費</td>
                        <td style={S.cell}><span style={{ fontWeight: "700", color: "#FF6B00" }}>{fmt(ACTIVATION_FEE)}</span>（一次性）</td>
                      </tr>
                      <tr>
                        <td style={S.label}>方案原價</td>
                        <td style={S.cell}><span style={{ textDecoration: "line-through", color: "#94a3b8" }}>{fmt(ORIGINAL_FEE)} / 月</span></td>
                      </tr>
                      <tr>
                        <td style={S.label}>方案特價</td>
                        <td style={S.cell}>
                          <span style={{ fontWeight: "800", color: "#FF6B00", fontSize: "14px" }}>{fmt(BASE_FEE)} / 月</span>
                          <span style={{ marginLeft: "8px", fontSize: "11px", color: "#475569" }}>同地點每多一棟 +NT$ 5,000/月</span>
                        </td>
                      </tr>
                      {units > 1 && (
                        <>
                          <tr>
                            <td style={{ ...S.label, background: "#FFF7ED" }}>本合約物件數</td>
                            <td style={S.cell}>共 <span style={{ color: "#FF6B00", fontWeight: "700" }}>{units} 棟</span>（基本 1 棟 + 多棟 {extraUnits} 棟）</td>
                          </tr>
                          <tr>
                            <td style={{ ...S.label, background: "#FFF7ED" }}>實際月付總額</td>
                            <td style={S.cell}>
                              {fmt(BASE_FEE)} + NT$ 5,000 × {extraUnits} 棟 = <span style={{ fontWeight: "800", color: "#FF6B00", fontSize: "14px" }}>{fmt(monthlyFee)} / 月</span>
                            </td>
                          </tr>
                        </>
                      )}
                      <tr>
                        <td style={S.label}>抽成方式</td>
                        <td style={S.cell}>所有訂單 20%</td>
                      </tr>
                    </>
                  )}
                  {selectedPlan === "plan5" && (
                    <>
                      <tr>
                        <td style={S.label}>啟動費</td>
                        <td style={S.cell}><span style={{ fontWeight: "700", color: "#FF6B00" }}>{fmt(ACTIVATION_FEE)}</span>（一次性）</td>
                      </tr>
                      <tr>
                        <td style={S.label}>方案月費</td>
                        <td style={S.cell}>
                          <span style={{ fontWeight: "800", color: "#FF6B00", fontSize: "14px" }}>{fmt(GROWTH_FEE)} / 月</span>
                          <span style={{ marginLeft: "8px", fontSize: "11px", color: "#475569" }}>中階方案，完整曝光與進單分析 · 同地點每多一棟 +NT$ 5,000/月</span>
                        </td>
                      </tr>
                      {units > 1 && (
                        <>
                          <tr>
                            <td style={{ ...S.label, background: "#FFF7ED" }}>本合約物件數</td>
                            <td style={S.cell}>共 <span style={{ color: "#FF6B00", fontWeight: "700" }}>{units} 棟</span>（基本 1 棟 + 多棟 {extraUnits} 棟）</td>
                          </tr>
                          <tr>
                            <td style={{ ...S.label, background: "#FFF7ED" }}>實際月付總額</td>
                            <td style={S.cell}>
                              {fmt(GROWTH_FEE)} + NT$ 5,000 × {extraUnits} 棟 = <span style={{ fontWeight: "800", color: "#FF6B00", fontSize: "14px" }}>{fmt(planMonthlyFee)} / 月</span>
                            </td>
                          </tr>
                        </>
                      )}
                      <tr>
                        <td style={S.label}>抽成方式</td>
                        <td style={S.cell}>所有訂單 20%</td>
                      </tr>
                    </>
                  )}
                  {selectedPlan === "plan2" && (
                    <>
                      <tr>
                        <td style={S.label}>啟動費</td>
                        <td style={S.cell}><span style={{ fontWeight: "700", color: "#FF6B00" }}>{fmt(ACTIVATION_FEE)}</span>（一次性，無月費）</td>
                      </tr>
                      <tr>
                        <td style={S.label}>保障營業額</td>
                        <td style={S.cell}>
                          {hasGuaranteed ? <span style={{ fontWeight: "800", color: "#FF6B00", fontSize: "14px" }}>{fmt(guaranteed)}</span> : <B v="" w={140} />}
                          <span style={{ marginLeft: "6px" }}>/ 月</span>
                          <span style={{ marginLeft: "8px", fontSize: "11px", color: "#475569" }}>（此金額以內，乙方不參與任何分潤）</span>
                        </td>
                      </tr>
                      <tr>
                        <td style={S.label}>超額分潤</td>
                        <td style={S.cell}>每月營業額超過保障營業額之部分，乙方抽成 50%，甲方保留 50%</td>
                      </tr>
                      <tr>
                        <td style={S.label}>營運範圍</td>
                        <td style={S.cell}><B v={form.campUnits} w={280} />{form.campUnits.trim() ? "" : "，實際依合作情況調整"}</td>
                      </tr>
                    </>
                  )}
                  {selectedPlan === "plan4" && (
                    <>
                      <tr>
                        <td style={S.label}>啟動費</td>
                        <td style={S.cell}><span style={{ fontWeight: "700", color: "#FF6B00" }}>{fmt(ACTIVATION_FEE)}</span>（一次性）</td>
                      </tr>
                      <tr>
                        <td style={S.label}>方案月費</td>
                        <td style={S.cell}>
                          <span style={{ fontWeight: "800", color: "#FF6B00", fontSize: "14px" }}>{fmt(LIGHT_FEE)} / 月</span>
                          <span style={{ marginLeft: "8px", fontSize: "11px", color: "#475569" }}>低月費入門，適合新開業／小型物件 · 同地點每多一棟 +NT$ 5,000/月</span>
                        </td>
                      </tr>
                      {units > 1 && (
                        <>
                          <tr>
                            <td style={{ ...S.label, background: "#FFF7ED" }}>本合約物件數</td>
                            <td style={S.cell}>共 <span style={{ color: "#FF6B00", fontWeight: "700" }}>{units} 棟</span>（基本 1 棟 + 多棟 {extraUnits} 棟）</td>
                          </tr>
                          <tr>
                            <td style={{ ...S.label, background: "#FFF7ED" }}>實際月付總額</td>
                            <td style={S.cell}>
                              {fmt(LIGHT_FEE)} + NT$ 5,000 × {extraUnits} 棟 = <span style={{ fontWeight: "800", color: "#FF6B00", fontSize: "14px" }}>{fmt(planMonthlyFee)} / 月</span>
                            </td>
                          </tr>
                        </>
                      )}
                      <tr>
                        <td style={S.label}>抽成方式</td>
                        <td style={S.cell}>所有訂單 25%</td>
                      </tr>
                    </>
                  )}
                  {selectedPlan === "plan3" && (
                    <>
                      <tr>
                        <td style={S.label}>方案費用</td>
                        <td style={S.cell}>由乙方每月支付甲方固定租金</td>
                      </tr>
                      <tr>
                        <td style={S.label}>備註</td>
                        <td style={{ ...S.cell, color: "#475569" }}>依物件條件評估，NT$30,000~150,000/月</td>
                      </tr>
                      <tr>
                        <td style={S.label}>抽成方式</td>
                        <td style={S.cell}>無抽成</td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <td style={S.label}>合約期間</td>
                    <td style={S.cell}>
                      {selectedPlan === "plan3" ? "60 / 84 / 120 個月（依雙方約定）" : <YearCircles selected={years} />}
                    </td>
                  </tr>
                  <tr>
                    <td style={S.label}>甲方負擔</td>
                    <td style={{ ...S.cell, color: "#475569" }}>
                      {selectedPlan === "plan3" ? "僅物業修繕維護（管家人事與水電由乙方負擔）" : "管家人事費用、現場物業水電維護"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ===== 方案服務等級（旗艦／成長／輕量） ===== */}
            {isFeeCommission && PLAN_SERVICE[selectedPlan] && (
              <div data-pdf-section="plan-service" style={{ marginBottom: "28px" }}>
                <div style={S.secTitle}>
                  <div style={S.dot("#0284C7")}></div>
                  方案服務等級（{planName}）
                </div>
                <table style={S.table}>
                  <tbody>
                    {PLAN_SERVICE[selectedPlan].map(([k, v]) => {
                      const hl = k === "社群經營" || k === "方案包裝";
                      return (
                      <tr key={k}>
                        <td style={hl ? { ...S.label, color: "#FF6B00", fontWeight: "800" } : S.label}>{hl ? "★ " : ""}{k}</td>
                        <td style={hl ? { ...S.cell, fontWeight: "600", background: "#FFF7ED" } : S.cell}>{v}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ===== 客服黃金時段真實數據洞察（所有月費方案皆呈現，旗艦特別 highlight） ===== */}
            {isFeeCommission && (
              <div data-pdf-section="prime-time-insight" data-pdf-block="" style={{ marginBottom: "28px" }}>
                <div style={S.secTitle}>
                  <div style={S.dot("#FF6B00")}></div>
                  客服黃金時段：真實進訊數據
                </div>
                <div style={{
                  background: isFlagship ? "linear-gradient(135deg,#FFF7ED 0%,#FFFFFF 60%,#FFF7ED 100%)" : "#F8FAFC",
                  border: isFlagship ? "2px solid #FF6B00" : "1px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "14px 16px",
                }}>
                  <div style={{ fontSize: "12px", fontWeight: "800", color: "#0A2540", marginBottom: "6px", lineHeight: "1.6" }}>
                    晚上 <span style={{ color: "#FF6B00" }}>21:00–23:00</span> 是新客首次詢問的<span style={{ color: "#FF6B00" }}>黃金時段</span>
                  </div>
                  <div style={{ fontSize: "11px", color: "#475569", marginBottom: "12px", lineHeight: "1.75" }}>
                    平日近 <b style={{ color: "#FF6B00" }}>19%</b>、假日高達 <b style={{ color: "#FF6B00" }}>21%</b> 的新客首次進訊落在這 3 小時內 —
                    比深夜低谷高出約 <b style={{ color: "#FF6B00" }}>15–20 倍</b>，卻是絕大多數同業客服打烊的時段。
                    （資料來源：<b style={{ color: "#0A2540" }}>森山寫照 × 哈庫納瑪塔塔</b> 客服系統實測，180 天 <b style={{ color: "#0A2540" }}>8,056 筆</b>進訊）
                  </div>
                  {/* 三段時段對比條 */}
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px", marginBottom: "10px" }}>
                    <tbody>
                      {[
                        { label: "深夜 03–05", sub: "多數同業都在服務", pct: 1,  color: "#94A3B8", hi: false },
                        { label: "下午 15–17", sub: "一般日間非高峰",   pct: 5,  color: "#0EA5E9", hi: false },
                        { label: "白天 12–14", sub: "一般日間高峰",     pct: 13, color: "#0284C7", hi: false },
                        { label: "晚上 21–23", sub: "新客首次詢問黃金時段", pct: 20, color: "#FF6B00", hi: true },
                      ].map((b) => (
                        <tr key={b.label}>
                          <td style={{ width: "110px", padding: "4px 8px 4px 0", verticalAlign: "middle" }}>
                            <div style={{ fontSize: "11px", fontWeight: "700", color: b.color, lineHeight: "1.25" }}>{b.label}</div>
                            <div style={{ fontSize: "9px", color: "#64748B", lineHeight: "1.25" }}>{b.sub}</div>
                          </td>
                          <td style={{ padding: "4px 0", verticalAlign: "middle" }}>
                            <div style={{ position: "relative", height: "18px", background: "#FFFFFF", border: "1px solid #e2e8f0", borderRadius: "9px", overflow: "hidden" }}>
                              <div style={{
                                width: `${(b.pct / 22) * 100}%`,
                                height: "100%",
                                background: b.hi ? "linear-gradient(90deg,#FFB86B 0%,#FF6B00 100%)" : b.color,
                                borderRadius: "9px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                paddingRight: "8px",
                                boxSizing: "border-box",
                              }}>
                                {b.pct >= 10 && (
                                  <span style={{ fontSize: "10px", fontWeight: "800", color: "#FFFFFF" }}>{b.pct}%</span>
                                )}
                              </div>
                              {b.pct < 10 && (
                                <span style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", left: `calc(${(b.pct / 22) * 100}% + 6px)`, fontSize: "10px", fontWeight: "800", color: b.color }}>
                                  {b.pct}%
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* 三方案覆蓋結論 */}
                  <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "6px 0", fontSize: "10px" }}>
                    <tbody>
                      <tr>
                        {[
                          { name: "輕量方案", text: "錯過黃金 3h", hi: false, active: isLight },
                          { name: "人客多成長方案", text: "✓ 完整接住", hi: true, active: isGrowth },
                          { name: "旗艦方案", text: "✓ 完整接住", hi: true, active: isFlagship },
                        ].map((p) => (
                          <td key={p.name} style={{
                            width: "33%",
                            padding: "8px 6px",
                            background: p.hi ? "linear-gradient(135deg,#FFF7ED 0%,#FFE4C4 100%)" : "#FFFFFF",
                            border: p.hi ? "2px solid #FF6B00" : "1px solid #e2e8f0",
                            borderRadius: "6px",
                            textAlign: "center",
                          }}>
                            <div style={{ fontSize: "10px", fontWeight: "800", color: p.hi ? "#FF6B00" : "#64748B", marginBottom: "2px" }}>
                              {p.name}{p.active ? "（本約）" : ""}
                            </div>
                            <div style={{ fontSize: "11px", fontWeight: "800", color: p.hi ? "#0A2540" : "#94A3B8" }}>
                              {p.text}
                            </div>
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                  <div style={{ fontSize: "10px", color: "#64748B", marginTop: "8px", textAlign: "center", lineHeight: "1.5" }}>
                    ＊每 5 位新客約有 1 位在 21–23 首次詢問，晚 2 小時打烊 =<b style={{ color: "#FF6B00" }}> 直接接住這批訂單</b>
                  </div>
                  <div style={{ fontSize: "9px", color: "#94A3B8", marginTop: "10px", paddingTop: "8px", borderTop: "1px solid rgba(255,107,0,0.2)", lineHeight: "1.6" }}>
                    <div style={{ fontWeight: "700", color: "#64748B", marginBottom: "2px" }}>資料統計範圍</div>
                    <div>‧ 哈庫納瑪塔塔：2026/04/26 14:07 – 2026/08/13 13:17</div>
                    <div>‧ 森山寫照：2025/07/18 16:57 – 2026/08/12 16:48</div>
                    <div>‧ 合計 180 天內 8,056 筆客服系統實際進訊，依時段分布計算</div>
                  </div>
                </div>
              </div>
            )}

            {/* ===== 見證場特別條款：營業額月費調整（旗艦方案專用，僅在客戶經理啟用時顯示） ===== */}
            {showRevTier && (
              <div data-pdf-section="rev-tier" style={{ marginBottom: "28px" }}>
                <div style={S.secTitle}>
                  <div style={S.dot("#FF6B00")}></div>
                  見證場特別條款：營業額月費調整
                </div>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#B8860B", background: "#FFFCF3", border: "1px solid #F5C24A", borderRadius: "6px", padding: "8px 12px", marginBottom: "10px" }}>
                  本條款為「見證場」個案專屬特別約定，<span style={{ color: "#0A2540" }}>僅適用於本合約</span>，不適用於其他標準合約。
                </div>
                <table style={{ ...S.table, borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ ...S.head("#0A2540"), width: "50px", textAlign: "center" }}></th>
                      <th style={S.head("#0A2540")}>當月營業額</th>
                      <th style={S.head("#0A2540")}>月費</th>
                      <th style={S.head("#0A2540")}>訂單抽成</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ ...S.cell, textAlign: "center", fontWeight: "700", color: "#0A2540", background: "#f8f9fa" }}>A</td>
                      <td style={S.cell}>{fmt(revTierThreshold)} 以下</td>
                      <td style={{ ...S.cell, fontWeight: "700", color: "#FF6B00" }}>{fmt(revTierBelowFee)} / 月</td>
                      <td style={S.cell}>
                        {revTierBelowCommission === 0
                          ? "合作後乙方帶入之訂單免抽成（0%）"
                          : `合作後乙方帶入之訂單抽成 ${revTierBelowCommission}%`}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ ...S.cell, textAlign: "center", fontWeight: "700", color: "#0A2540", background: "#f8f9fa" }}>B</td>
                      <td style={S.cell}>{fmt(revTierThreshold)} 以上</td>
                      <td style={{ ...S.cell, fontWeight: "700", color: "#FF6B00" }}>{fmt(planBaseFee)} / 月</td>
                      <td style={S.cell}>合作後乙方帶入之訂單抽成 {commissionRate}%</td>
                    </tr>
                  </tbody>
                </table>
                <div style={{ marginTop: "8px", fontSize: "11px", color: "#475569", lineHeight: "1.7" }}>
                  * 每月結算時，依當月營業額判斷適用級距，取代標準方案之月費與抽成規範。
                </div>
              </div>
            )}

            {/* ===== 早鳥優惠（月付）：見證場保障方案 ===== */}
            {isFeeCommission && (
              <div data-pdf-section="earlybird" style={{ marginBottom: "28px" }}>
                <div style={S.secTitle}>
                  <div style={S.dot("#F5C24A")}></div>
                  早鳥優惠方案
                </div>
                <div style={{ border: "1px solid #F5C24A", background: "#FFFCF3", borderRadius: "8px", padding: "14px 16px", fontSize: "12px", color: "#0A2540", lineHeight: "2" }}>
                  <div style={{ marginBottom: "4px", fontWeight: "700", color: "#B8860B" }}>
                    早鳥限量業主專屬：獨享「見證場保障方案」，更安心更有保障
                  </div>
                  <div style={{ marginBottom: "4px" }}>
                    合作期間，如當月營業額未達雙方約定之月營收門檻，該月將自動改採較低之月費／訂單抽成，降低甲方營運壓力，讓雙方合作更有保障。
                  </div>
                  <div style={{ fontSize: "10px", color: "#94a3b8" }}>
                    ※ 具體門檻、未達門檻適用之月費及抽成，以本合約「見證場特別條款：營業額月費調整」所載內容為準。
                  </div>
                  <div style={{ marginTop: "4px" }}>
                    早鳥適用月份：<B v={form.earlyBirdMonth} w={180} />
                  </div>
                </div>
              </div>
            )}

            {/* ===== 分潤試算範例（保障分潤） ===== */}
            {selectedPlan === "plan2" && (
              <div data-pdf-section="example" style={{ marginBottom: "28px" }}>
                <div style={S.secTitle}>
                  <div style={S.dot("#16a34a")}></div>
                  試算範例：以每月營業額 {fmt(exTotal)} 為例（假設保障營業額為 {fmt(exG)}）
                </div>
                <table style={S.table}>
                  <tbody>
                    <tr>
                      <td style={S.label}>當月總營業額</td>
                      <td style={S.cell}>{fmt(exTotal)}</td>
                    </tr>
                    <tr>
                      <td style={S.label}>減：保障營業額（甲方全額保留）</td>
                      <td style={S.cell}>- {fmt(exG)}</td>
                    </tr>
                    <tr>
                      <td style={S.label}>超額部分</td>
                      <td style={S.cell}>{fmt(exTotal - exG)}</td>
                    </tr>
                    <tr>
                      <td style={{ ...S.label, background: "#f0fdf4" }}>甲方實際可得（保障 + 超額 50%）</td>
                      <td style={{ ...S.cell, fontWeight: "700", color: "#16a34a" }}>
                        {fmt(exG)} + {fmt((exTotal - exG) / 2)} = {fmt(exG + (exTotal - exG) / 2)}
                      </td>
                    </tr>
                    <tr>
                      <td style={S.label}>乙方實際可得（超額 50%）</td>
                      <td style={S.cell}>{fmt((exTotal - exG) / 2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* ===== 主力方案比較（旗艦／成長／輕量） ===== */}
            <div data-pdf-section="comparison" style={{ marginBottom: "28px" }}>
              <div style={S.secTitle}>
                <div style={S.dot("#FF6B00")}></div>
                主力方案比較（輕量 / 人客多成長 / 旗艦）
              </div>
              {selectedPlan !== "plan3" && (
                <div style={{ fontSize: "11px", color: "#475569", marginBottom: "8px" }}>
                  {isFlagship
                    ? `本合約採用旗艦方案，特價 ${fmt(BASE_FEE)}/月（原價 ${fmt(ORIGINAL_FEE)}），所有訂單抽成 20%。`
                    : isGrowth
                    ? `本合約採用人客多成長方案，月費 ${fmt(GROWTH_FEE)}，所有訂單抽成 20%。`
                    : isLight
                    ? `本合約採用輕量方案，月費 ${fmt(LIGHT_FEE)}，所有訂單抽成 25%。`
                    : "本合約採用保障分潤方案，僅需一次性啟動費，無月費。以下為主力方案比較，供參考。"}
                </div>
              )}
              {(() => {
                const cmpPlans = ["plan4", "plan5", "plan1"];
                const cmpHeads = [
                  ["輕量方案", "#0284C7"],
                  ["人客多成長方案", "#0D9488"],
                  ["旗艦方案", "#0A2540"],
                ];
                const rows = [
                  ["月費", "NT$38,000", "NT$66,000→56,000", "NT$108,000→78,000"],
                  ["啟動費", "50,000（一次性）", "50,000（一次性）", "50,000（一次性）"],
                  ["訂單抽成", "25%", "20%", "20%"],
                  ["客服時段", "10:00–21:00", "10:00–23:00", "10:00–23:00"],
                  ["夜間 21–23 新客覆蓋", "未涵蓋", "完整接單（+21–23）", "完整接單（+21–23）"],
                  ["保障影片", "20 隻", "48 隻", "60 隻"],
                  ["影片保障價值", "約 20 萬", "約 48 萬", "約 60 萬"],
                  ["KOL 導流", "15 位以上", "40 位以上", "55 位以上"],
                  ["社群小編經營", "✔", "✔", "✔"],
                  ["客單價提升方案包裝設計", "✔", "✔", "✔"],
                  ["多棟庫存調度／跨棟導流", "—", "—", "✔"],
                  ["Google 評價優化", "個人用", "個人用", "團隊用"],
                ];
                const cell = (i) => ({ padding: "6px 8px", border: "1px solid #e2e8f0", textAlign: "center", color: "#0A2540", fontSize: "10px", background: selectedPlan === cmpPlans[i] ? "#FFF7ED" : "white" });
                const lbl = { padding: "6px 10px", border: "1px solid #e2e8f0", fontWeight: "600", color: "#0A2540", fontSize: "10px" };
                return (
                  <table style={{ ...S.table, fontSize: "10px", tableLayout: "fixed" }}>
                    <thead>
                      <tr>
                        <th style={{ ...S.head("#0A2540"), textAlign: "left", width: "22%", fontSize: "10px" }}>項目</th>
                        {cmpHeads.map(([name, base], i) => (
                          <th key={name} style={{ ...S.head(selectedPlan === cmpPlans[i] ? "#FF6B00" : base), textAlign: "center", fontSize: "10px" }}>
                            {name}{selectedPlan === cmpPlans[i] ? "（本約）" : ""}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(([label, ...vals]) => (
                        <tr key={label}>
                          <td style={lbl}>{label}</td>
                          {vals.map((v, i) => {
                            const isCsHi = label === "客服時段" && i >= 1;
                            const isNightHi = label === "夜間 21–23 新客覆蓋" && i >= 1;
                            const style = (isCsHi || isNightHi) ? { ...cell(i), fontWeight: "800", color: "#FF6B00" } : cell(i);
                            const text = isCsHi ? `${v}（含夜間）` : v;
                            return (<td key={i} style={style}>{text}</td>);
                          })}
                        </tr>
                      ))}
                      <tr>
                        <td style={lbl}>合約期間</td>
                        {cmpPlans.map((p, i) => (
                          <td key={p} style={cell(i)}>
                            <MonthCircles months={[12, 24, 36]} selectedIdx={years - 1} active={selectedPlan === p} />
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                );
              })()}
              <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: "6px" }}>
                * 客服時段為週一至五；週六、日為 10:00–20:00。保障影片依實際情況調整，避免影響營收，不一定拍滿。另有「保障分潤方案」（0 月費、超額五五分潤）與「回租方案」（乙方每月支付甲方租金），詳情請洽簽約顧問。
              </div>
            </div>

            {/* ===== 付款方式與服務生效 ===== */}
            {selectedPlan !== "plan3" && (
              <div data-pdf-section="payment-flow" style={{ marginBottom: "28px" }}>
                <div style={S.secTitle}>
                  <div style={S.dot("#0A2540")}></div>
                  付款方式與服務生效
                </div>
                <div style={{ fontSize: "11px", color: "#475569", marginBottom: "8px" }}>
                  {isFeeCommission
                    ? "說明：甲方付款予乙方後，服務即刻生效。首次付款日起算為服務啟動日，後續每月依約定日期前完成付款，啟動該月服務。"
                    : "說明：甲方支付一次性啟動費後，服務即刻生效。後續依每月營業額超額部分進行分潤結算。"}
                </div>
                <table style={S.table}>
                  <tbody>
                    {(isFeeCommission
                      ? [
                          ["付款方向", "甲方 → 乙方"],
                          ["首次付款", "付款日起，服務即刻生效"],
                          ["對帳日", "每月 5 日前，乙方提供上月訂單抽成明細予甲方確認"],
                          ["匯款日", "每月 10 日前，甲方完成當月月付費用及上月訂單抽成匯款"],
                          ["月付金額", `${fmt(planMonthlyFee)}${isFlagship ? "（特價" : ""}${units > 1 ? `${isFlagship ? "，" : "（"}含多棟 ${units} 棟${isFlagship ? "" : "）"}` : ""}${isFlagship ? "）" : ""} + 訂單抽成 ${commissionRate}%`],
                        ]
                      : [
                          ["啟動費", `${fmt(ACTIVATION_FEE)}（一次性，付款後服務即刻啟動）`],
                          ["月費", "無"],
                          ["對帳日", "每月 5 日前，乙方提供上月營業額明細予甲方確認"],
                          ["匯款日", "每月 10 日前，甲方依上月營業額超額部分完成分潤匯款"],
                          ["營運範圍", form.campUnits.trim() ? `${form.campUnits} 全數納入營運` : "依雙方約定之範圍全數納入營運"],
                        ]
                    ).map(([label, v]) => (
                      <tr key={label}>
                        <td style={S.label}>{label}</td>
                        <td style={S.cell}>{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* ===== 甲乙雙方責任區分 ===== */}
            <div data-pdf-section="duties" style={{ marginBottom: "28px" }}>
              <div style={S.secTitle}>
                <div style={S.dot("#FF6B00")}></div>
                甲乙雙方責任區分
              </div>
              <div style={{ display: "flex", gap: "16px" }}>
                <div style={{ flex: 1, border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
                  <div style={{ background: "#0A2540", color: "white", fontWeight: "700", fontSize: "12px", padding: "8px 12px" }}>甲方負責</div>
                  <div style={{ padding: "10px 14px", fontSize: "11px", color: "#475569", lineHeight: "1.9" }}>
                    {OWNER_DUTIES.map((d) => <div key={d}>• {d}</div>)}
                  </div>
                </div>
                <div style={{ flex: 1, border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
                  <div style={{ background: "#FF6B00", color: "white", fontWeight: "700", fontSize: "12px", padding: "8px 12px" }}>乙方負責</div>
                  <div style={{ padding: "10px 14px", fontSize: "11px", color: "#475569", lineHeight: "1.9" }}>
                    {companyDuties.map((d) => <div key={d}>• {d}</div>)}
                  </div>
                </div>
              </div>
            </div>

            {/* ===== 乙方服務內容 ===== */}
            <div data-pdf-section="services" style={{ marginBottom: "28px" }}>
              <div style={S.secTitle}>
                <div style={S.dot("#0A2540")}></div>
                乙方服務內容（所有方案均包含）
              </div>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={{ ...S.head("#0A2540"), textAlign: "left", width: "42%" }}>服務項目</th>
                    <th style={{ ...S.head("#0A2540"), textAlign: "left" }}>說明</th>
                  </tr>
                </thead>
                <tbody>
                  {SERVICES.map(([service, desc]) => {
                    const hl = SERVICE_HL.includes(service);
                    return (
                    <tr key={service}>
                      <td style={{ padding: "7px 12px", border: "1px solid #e2e8f0", color: hl ? "#FF6B00" : "#0A2540", fontWeight: hl ? "800" : "500", background: hl ? "#FFF7ED" : "transparent" }}>{hl ? "★ " : ""}{service}</td>
                      <td style={{ padding: "7px 12px", border: "1px solid #e2e8f0", color: hl ? "#0A2540" : "#475569", fontWeight: hl ? "600" : "400", background: hl ? "#FFF7ED" : "transparent" }}>{desc}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ===== 乙方與甲方帳號配合 ===== */}
            <div data-pdf-section="accounts" style={{ marginBottom: "28px" }}>
              <div style={S.secTitle}>
                <div style={S.dot("#FF6B00")}></div>
                乙方與甲方帳號配合
              </div>
              <div style={{ ...S.body, marginBottom: "8px" }}>
                合作期間，配合新定價策略，乙方與甲方共同管理新的人客多精選系列帳戶。甲方原先之帳戶將關閉或轉型為個人
                IP 帳號，並由雙方相互設定管理員，確保行銷漏斗之完整性。
              </div>
              <div style={{ ...S.body }}>
                <div style={{ fontWeight: "600", color: "#0A2540", marginBottom: "2px" }}>帳戶範圍包含：</div>
                <div>• Facebook 粉絲專頁</div>
                <div>• Instagram 官方帳號</div>
                <div>• LINE@ 官方帳號</div>
                <div>• 其他雙方對外曝光之管道</div>
              </div>
            </div>

            {/* ===== 合約條款 ===== */}
            <div data-pdf-section="terms" style={{ marginBottom: "28px" }}>
              <div style={S.secTitle}>
                <div style={S.dot("#0A2540")}></div>
                合約條款
              </div>
              {selectedPlan !== "plan3" ? (
                <div style={S.termsBody}>
                  <div data-pdf-block="" style={S.clause}>1. 本合約自雙方簽署之日起生效，合約期間依雙方圈選之年限為準（<span style={{ fontWeight: "700", color: "#FF6B00" }}>{years} 年</span>）。</div>
                  <div data-pdf-block="" style={S.clause}>2. 乙方應依本報價單所列服務內容，全力協助甲方民宿之品牌管理與營銷推廣，並就乙方客源衍生之現場營運風險與甲方共同把關、共同承擔。</div>
                  <div data-pdf-block="" style={S.clause}>3. 甲方應提供乙方執行服務所需之必要資訊、帳號權限及現場配合。</div>
                  <div data-pdf-block="" style={S.clause}>4. 乙方提供專人客服團隊（每日 {csHours}，週六、日 10:00–20:00）接單服務，並定期優化接單流程與服務品質；AI 智能接單系統正式上線後，本合約簽約客戶享有優先使用權。</div>
                  <div data-pdf-block="" style={S.clauseHi}>5. 甲方應提供銀行帳戶存摺封面及帳戶相關資料予乙方，作為訂房收款之用。每日接單金流將由乙方顧問配合人客多 PMS 系統與甲方共同控管所有接單場域之訂單資訊。後續乙方開發之信用卡金流自動對帳功能，簽約客戶可免費升級使用，不另行收費。</div>
                  <div data-pdf-block="" style={S.clause}>6. 前條收款管理規範，經由人客多訂房系統之訂單不在此限。</div>
                  <div data-pdf-block="" style={S.clause}>7. 雙方對於合作過程中所知悉之營業秘密及客戶資料，負有保密義務。</div>
                  <div data-pdf-block="" style={S.clause}>8. 本合約期滿後自然終止；如雙方欲續約，應另行書面議定。</div>
                  {form.minRevEnabled && (
                    <div data-pdf-block="" style={S.clauseMed}>
                      9.【連續未達業績之解約權】自正式收費起算，如連續四個月皆未能達成營業額 {hasMinRev ? <span style={{ fontWeight: "700", color: "#FF6B00" }}>{fmt(minRev)}</span> : <B v="" w={110} />} 以上業績，甲方或乙方均有權以書面通知對方解除本合約，不受違約條款之規範。
                    </div>
                  )}
                  {selectedPlan === "plan2" ? (
                  <div data-pdf-block="" style={S.clause}>
                    {form.minRevEnabled ? "10" : "9"}.【違約條款】合約期間內，甲方若單方面提前終止合作，應支付違約金 <span style={{ fontWeight: "700", color: "#FF6B00" }}>{fmt(ACTIVATION_FEE)}</span>（相當於啟動費），並結清終止日前已發生之超額分潤款項。
                  </div>
                  ) : (
                  <div data-pdf-block="" style={S.clause}>
                    {form.minRevEnabled ? "10" : "9"}.【違約條款】合約期間內，甲方若單方面提前終止合作，應支付以下違約金（依合約年限適用）：
                    <div style={{ margin: "6px 0 4px 14px" }}>
                      {[1, 2, 3].map((y) => (
                        <div key={y} style={{ fontWeight: y === years ? "700" : "400", color: y === years ? "#FF6B00" : "#475569" }}>
                          {y === years ? "✓ " : "　"}{y} 年約：違約金 = {y} 個月月費 {fmt(penaltyBase * y)} + 啟動費 {fmt(ACTIVATION_FEE)} = {fmt(penalty(y))}
                        </div>
                      ))}
                    </div>
                  </div>
                  )}
                  <div data-pdf-block="" style={S.clause}>{form.minRevEnabled ? "11" : "10"}. 本合約之調整應經雙方書面合意；未盡事宜依中華民國法律辦理。如生爭議，雙方應誠信協商，協商未果時合意以<span style={{ fontWeight: "700", color: "#0A2540" }}>{form.court}</span>為第一審管轄法院。</div>
                </div>
              ) : (
                <div style={S.termsBody}>
                  <div data-pdf-block="" style={S.clause}>1. 本合約自雙方簽署之日起生效，合約期間依雙方約定之年限為準。</div>
                  <div data-pdf-block="" style={S.clause}>2. 乙方應依本報價單所列服務內容，全力協助甲方物件之品牌管理與營銷推廣，管家人事與物業水電由乙方負擔。</div>
                  <div data-pdf-block="" style={S.clause}>3. 甲方應提供乙方執行服務所需之必要資訊、帳號權限及現場配合。</div>
                  <div data-pdf-block="" style={S.clause}>4. 乙方應依約定於每月固定日期支付甲方租金，租金金額依雙方議定之合約為準。</div>
                  <div data-pdf-block="" style={S.clause}>5. 雙方對於合作過程中所知悉之營業秘密及客戶資料，負有保密義務。</div>
                  <div data-pdf-block="" style={S.clause}>6. 合約期滿前 30 日，任一方如不續約應以書面通知對方。</div>
                  <div data-pdf-block="" style={S.clause}>7. 本合約未盡事宜，依中華民國相關法律規定辦理。</div>
                  <div data-pdf-block="" style={S.clause}>8. 如有爭議，雙方同意以<span style={{ fontWeight: "700", color: "#0A2540" }}>{form.court}</span>為第一審管轄法院。</div>
                </div>
              )}
            </div>

            {/* ===== 付款規範 ===== */}
            {selectedPlan !== "plan3" && (
              <div data-pdf-section="payment-terms" style={{ marginBottom: "28px" }}>
                <div style={S.secTitle}>
                  <div style={S.dot("#FF6B00")}></div>
                  {planName}付款規範
                </div>

                <div data-pdf-block="" style={{ fontSize: "14px", fontWeight: "700", color: "#0A2540", marginBottom: "8px" }}>一、甲方付款予乙方之規範</div>
                <div style={{ ...S.termsBody, background: "#f8f9fa", borderRadius: "8px", padding: "14px 16px", border: "1px solid #e2e8f0", marginBottom: "14px" }}>
                  {isFeeCommission ? (
                    <>
                      <div data-pdf-block="" style={{ marginBottom: "6px", color: "#0A2540" }}>說明：甲方依約定時間將月費及訂單抽成匯予乙方，付款完成後該月服務即刻啟動。</div>
                      <div data-pdf-block="" style={S.clause}>1. 首次付款：甲方於簽約後完成首期付款，付款日起服務即刻生效。</div>
                      <div data-pdf-block="" style={S.clause}>2. 後續付款：甲方應於每月 10 日前，將當月月費 {fmt(planMonthlyFee)}{isFlagship ? "（或依特別條款適用之金額）" : ""}匯入乙方指定之金融機構帳戶，啟動該月服務。</div>
                      <div data-pdf-block="" style={S.clause}>3. 訂單抽成：乙方應於每月月初五日（含）前，完成前一個月之訂單抽成金額計算（所有訂單之 {commissionRate}%），並將明細以書面或電子方式通知甲方。甲方應於每月 10 日前，將前一個月之訂單抽成款項一併撥付至乙方指定帳戶。</div>
                      <div data-pdf-block="" style={S.clause}>4. 甲方逾期未依前項約定期限完成繳款者，乙方得立即停止提供本合約所定之一切服務，並不負任何因服務中斷所生之賠償責任。</div>
                      <div data-pdf-block="" style={S.clause}>5. 甲方逾期未付款經乙方書面催告後仍未於三日內清償者，乙方得逕行解除本合約，並就未付款項及遲延利息（按年利率百分之五計算），循民事訴訟或非訟程序向甲方追償，甲方並應負擔因此所生之一切訴訟費用及律師報酬。</div>
                    </>
                  ) : (
                    <>
                      <div data-pdf-block="" style={{ marginBottom: "6px", color: "#0A2540" }}>說明：甲方支付一次性啟動費後即啟動服務，後續每月依營業額超額部分之 50% 撥付分潤予乙方。</div>
                      <div data-pdf-block="" style={S.clause}>1. 啟動費：甲方於簽約後支付一次性啟動費 {fmt(ACTIVATION_FEE)}，付款日起服務即刻生效。</div>
                      <div data-pdf-block="" style={S.clause}>2. 分潤結算：乙方應於每月月初五日（含）前，完成上月度之營業額計算，並將明細以書面或電子方式通知甲方。</div>
                      <div data-pdf-block="" style={S.clause}>3. 甲方應於每月 10 日前，將前一個月營業額超過保障營業額部分之 50% 分潤款項，撥付至乙方指定之金融機構帳戶。</div>
                      <div data-pdf-block="" style={S.clause}>4. 甲方逾期未依前項約定期限完成繳款者，乙方得立即停止提供本合約所定之一切服務，並不負任何因服務中斷所生之賠償責任。</div>
                      <div data-pdf-block="" style={S.clause}>5. 甲方逾期未付款經乙方書面催告後仍未於三日內清償者，乙方得逕行解除本合約，並就未付款項及遲延利息（按年利率百分之五計算），循民事訴訟或非訟程序向甲方追償，甲方並應負擔因此所生之一切訴訟費用及律師報酬。</div>
                    </>
                  )}
                </div>

                <div data-pdf-block="" style={{ fontSize: "14px", fontWeight: "700", color: "#0A2540", marginBottom: "8px" }}>二、乙方匯款予甲方之規範</div>
                <div style={{ ...S.termsBody, background: "#f8f9fa", borderRadius: "8px", padding: "14px 16px", border: "1px solid #e2e8f0", marginBottom: "14px" }}>
                  <div data-pdf-block="" style={{ marginBottom: "6px", color: "#0A2540" }}>說明：待乙方開發之信用卡金流自動對帳服務上線後，訂單金流將由系統自動處理，屆時乙方依以下規範將甲方應得款項匯予甲方。</div>
                  <div data-pdf-block="" style={S.clause}>1. 乙方應於每月月初五日（含）前，完成上月度之營收金額計算，並將明細以書面或電子方式通知甲方。</div>
                  <div data-pdf-block="" style={S.clause}>2. 乙方應於每月 10 日前，將前一個月，雙方約定甲方應得之款項，匯入甲方指定之金融機構帳戶。</div>
                  <div data-pdf-block="" style={S.clause}>3. 乙方逾期未依前項約定期限完成匯款者，甲方得以書面催告乙方，乙方仍未於三日內清償者，甲方得逕行解除本合約，並就未付款項及遲延利息（按年利率百分之五計算），循民事訴訟或非訟程序向乙方追償，乙方並應負擔因此所生之一切訴訟費用及律師報酬。</div>
                  <div data-pdf-block="" style={{ marginTop: "8px", color: "#475569" }}>備註：待乙方開發之信用卡金流自動對帳服務正式上線後，上述乙方匯款予甲方之流程將由雙方共同討論採用時機與執行時間後而定。</div>
                </div>

                <div data-pdf-block="">
                  <div style={{ fontSize: "14px", fontWeight: "700", color: "#0A2540", marginBottom: "8px" }}>三、不續約與合約終止機制</div>
                  <div style={{ ...S.termsBody, background: "#f8f9fa", borderRadius: "8px", padding: "14px 16px", border: "1px solid #e2e8f0" }}>
                    <div style={{ fontWeight: "600", color: "#0A2540", marginBottom: "2px" }}>不續約方式</div>
                    <div style={{ marginBottom: "8px" }}>任一方若欲終止合作，須於<span style={{ fontWeight: "700", color: "#FF6B00" }}>合約到期前 30 日</span>，以書面方式通知對方，經雙方書面同意後方可不續約。書面方式為電子郵件或者紙本信件說明。經雙方書面同意不續約後，合約於該期屆滿日自然終止，不另收取違約金。</div>
                    <div style={{ fontWeight: "600", color: "#0A2540", marginBottom: "2px" }}>因天災、政府法令變更之合約終止</div>
                    <div>倘因天災、重大意外，或政府法令變更，致使現場無法繼續運營，或難以達成本合約約定之營運目標者，雙方得經協調合意後提前終止合作。此情形下，雙方應就已發生之費用及分潤進行結算，不適用違約條款。</div>
                  </div>
                </div>
              </div>
            )}

            {/* ===== 特別條款 ===== */}
            {specialLines.length > 0 && (
              <div data-pdf-section="special-terms" style={{ marginBottom: "28px" }}>
                <div style={S.secTitle}>
                  <div style={S.dot("#B8860B")}></div>
                  特別條款
                </div>
                <div style={{ ...S.termsBody, background: "#FFFCF3", border: "1px solid #F5C24A", borderRadius: "8px", padding: "14px 16px" }}>
                  {specialLines.map((line, i) => (
                    <div key={`${i}-${line}`} style={S.clause}>{specialLines.length > 1 ? `${i + 1}. ` : ""}{line}</div>
                  ))}
                </div>
              </div>
            )}

            {/* ===== 雙方銀行匯款資訊 ===== */}
            <div data-pdf-section="bank-info-b" style={{ marginBottom: "28px" }}>
              <div style={S.secTitle}>
                <div style={S.dot("#FF6B00")}></div>
                雙方銀行匯款資訊
              </div>
              <div style={{ fontSize: "12px", fontWeight: "700", background: "#0A2540", color: "white", padding: "8px 12px", borderRadius: "6px 6px 0 0" }}>
                乙方（人客多股份有限公司）銀行帳戶
              </div>
              <table style={S.table}>
                <tbody>
                  <tr>
                    <td style={{ ...S.label, width: "120px" }}>銀行名稱</td>
                    <td style={S.cell}>陽信銀行（Sunny Bank）</td>
                    <td style={{ ...S.label, width: "120px" }}>金融代號</td>
                    <td style={S.cell}>108</td>
                  </tr>
                  <tr>
                    <td style={S.label}>分行名稱</td>
                    <td style={S.cell}>羅東分行</td>
                    <td style={S.label}>分行代碼</td>
                    <td style={S.cell}>053</td>
                  </tr>
                  <tr>
                    <td style={S.label}>戶名</td>
                    <td style={{ ...S.cell, fontWeight: "700" }}>人客多股份有限公司</td>
                    <td style={S.label}>帳號</td>
                    <td style={{ ...S.cell, color: "#FF6B00", fontWeight: "700" }}>12145-000035-3</td>
                  </tr>
                </tbody>
              </table>
              <div style={{ marginTop: "8px", fontSize: "11px", fontWeight: "600", color: "#0A2540", marginBottom: "4px" }}>乙方存摺封面：</div>
              <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "8px", background: "#f8f9fa", width: "296px" }}>
                <img src={BANK_PASSBOOK_BASE64} alt="乙方存摺封面 - 陽信銀行" style={{ display: "block", width: "280px", height: "180px", objectFit: "cover", borderRadius: "4px" }} />
              </div>
            </div>

            <div data-pdf-section="bank-info-a" style={{ marginBottom: "28px" }}>
              <div style={{ fontSize: "12px", fontWeight: "700", background: "#f8f9fa", border: "1px solid #e2e8f0", padding: "8px 12px", borderRadius: "6px 6px 0 0", color: "#0A2540" }}>
                甲方（業主）銀行帳戶
              </div>
              <table style={S.table}>
                <tbody>
                  <tr>
                    <td style={{ ...S.label, width: "120px" }}>銀行名稱</td>
                    <td style={S.cell}><B v={form.bankName} w={100} /></td>
                    <td style={{ ...S.label, width: "120px" }}>金融代號</td>
                    <td style={S.cell}><B v={form.bankCode} w={100} /></td>
                  </tr>
                  <tr>
                    <td style={S.label}>分行名稱</td>
                    <td style={S.cell}><B v={form.branchName} w={100} /></td>
                    <td style={S.label}>分行代碼</td>
                    <td style={S.cell}><B v={form.branchCode} w={100} /></td>
                  </tr>
                  <tr>
                    <td style={S.label}>戶名</td>
                    <td style={S.cell}><B v={form.accountName} w={100} /></td>
                    <td style={S.label}>帳號</td>
                    <td style={S.cell}><B v={form.accountNumber} w={100} /></td>
                  </tr>
                </tbody>
              </table>
              <div style={{ marginTop: "8px", fontSize: "11px", fontWeight: "600", color: "#0A2540", marginBottom: "4px" }}>甲方存摺封面黏貼處：</div>
              <div style={{ border: "2px dashed #cbd5e1", borderRadius: "8px", padding: "40px", background: "#f8f9fa", textAlign: "center", minHeight: "220px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ color: "#94a3b8", fontSize: "12px" }}>請將甲方銀行存摺封面影本黏貼於此處</div>
              </div>
            </div>

            {/* ===== 合約簽署 ===== */}
            <div data-pdf-section="signature" style={{ marginTop: "40px" }}>
              <div style={{ fontSize: "13px", fontWeight: "700", background: "#0A2540", color: "white", padding: "10px 14px", borderRadius: "8px 8px 0 0" }}>
                合約簽署
              </div>
              <div style={{ border: "1px solid #e2e8f0", borderTop: "none", borderRadius: "0 0 8px 8px", padding: "20px 24px" }}>
                <div style={{ fontSize: "11px", color: "#475569", marginBottom: "20px" }}>本合約一式兩份，由甲乙雙方各執一份，自簽署之日起生效。</div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "48px" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "12px", fontWeight: "700", color: "#0A2540", marginBottom: "14px" }}>甲方（業主）</div>
                    <div style={{ fontSize: "11px", color: "#475569", marginBottom: "4px" }}>簽約人：<B v={form.ownerName} w={120} /></div>
                    <div style={{ fontSize: "11px", color: "#475569", marginTop: "20px" }}>公司大章：</div>
                    <div style={{ borderBottom: "1px solid #cbd5e1", marginTop: "56px", marginBottom: "8px" }}></div>
                    <div style={{ fontSize: "11px", color: "#475569" }}>小章（個人章）：</div>
                    <div style={{ borderBottom: "1px solid #cbd5e1", marginTop: "48px", marginBottom: "8px" }}></div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "12px", fontWeight: "700", color: "#0A2540", marginBottom: "14px" }}>乙方（人客多股份有限公司）</div>
                    <div style={{ fontSize: "11px", color: "#475569", marginBottom: "4px" }}>簽約人：<B v={form.consultant} w={120} /></div>
                    <div style={{ fontSize: "11px", color: "#475569", marginTop: "20px", marginBottom: "4px" }}>公司大章：</div>
                    <img src={STAMP_COMPANY} alt="人客多股份有限公司 公司章" style={{ width: "72px", height: "72px", objectFit: "contain", marginBottom: "8px" }} />
                    <div style={{ fontSize: "11px", color: "#475569", marginBottom: "4px" }}>負責人小章：</div>
                    <img src={STAMP_PERSONAL} alt="負責人章" style={{ width: "60px", height: "60px", objectFit: "contain", marginBottom: "8px" }} />
                  </div>
                </div>
                <div style={{ marginTop: "24px", textAlign: "center", fontSize: "13px", fontWeight: "600", color: "#0A2540" }}>
                  簽約日期：{signDateObj
                    ? `${signDateObj.getFullYear()} 年 ${signDateObj.getMonth() + 1} 月 ${signDateObj.getDate()} 日`
                    : `${today.getFullYear()} 年 ＿＿＿ 月 ＿＿＿ 日`}
                </div>
              </div>
            </div>

            {/* ===== Footer ===== */}
            <div style={{ marginTop: "40px", paddingTop: "16px", borderTop: "2px solid #FF6B00", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: "10px", color: "#94a3b8" }}>
                人客多股份有限公司
              </div>
              <div style={{ fontSize: "10px", color: "#94a3b8" }}>
                Lakeze 人客多
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractPage;
